import type { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { BrowserCacheLocation, PublicClientApplication } from "@azure/msal-browser";

// IPublicClientApplication は挙動を持つオブジェクトなので State に入れられない
let publicClientApplication: IPublicClientApplication | null = null;

/**
 * loginRedirect などで引き渡す scope
 */
const MsalScopes = ["openid", "profile"]; // B2C では "user.read" が対象でないょうに見える

type handleRedirectErrorType = {
  errorCode?: string
  errorMessage?: string
  subError?: string
  name?: string
  correlationId?: string
}

export const useMsal = () => {
  const initialRouteState = useInitialRouteState();
  const handleRedirectError = ref<handleRedirectErrorType>();

  /**
   * publicClientApplication が生成されていなければ生成して初期化する
   * 生成されていればそのまま返す
   */
  const getPublicClientApplicationSub = async (): Promise<IPublicClientApplication | null> => {
    if (!import.meta.client) {
      return null;
    }

    if (publicClientApplication) {
      return publicClientApplication;
    }

    const circlemsConfig = useCirclemsConfig();
    const config = circlemsConfig.get();
    const msalConfig = {
      auth: config.browserAuthOptions,
      cache: {
        cacheLocation: BrowserCacheLocation.SessionStorage, // 認証情報をタブ間で共有しない
        storeAuthStateInCookie: false,
      },
      system: {
        tokenRenewalOffsetSeconds: 300,
        // loggerOptions: {
        //   loggerCallback: (level: unknown, message: string) => {
        //     console.log("msal loggerCallback", level, message);
        //   },
        // },
      }
    };
    publicClientApplication = new PublicClientApplication(msalConfig);
    await publicClientApplication.initialize();
    // handleRedirectPromise 内部で location.hash をクリアする。この時 nuxt3 処理が遷移してしまい後続処理に進まないため State を利用している
    const originalHash = initialRouteState.getInitialHash();
    // console.log("location.hash", originalHash);
    try {
      await publicClientApplication.handleRedirectPromise(originalHash);
    }
    catch (error: unknown) {
      // Azure AD B2C でユーザーがキャンセルした場合の処理
      // {"errorCode":"access_denied","errorMessage":"AADB2C90091: ユーザーが申告情報の入力を取り消しました.\\r\\nCorrelation ID: 5e0d6206-41a2-4338-b882-96a9a40cddd5\\r\\nTimestamp: 2025-06-05 07:09:38Z\\r\\n","subError":"","name":"ServerError","correlationId":"01973eec-77f3-7bf8-aa80-930b97c5157c"}
      // https://learn.microsoft.com/ja-jp/azure/active-directory-b2c/error-codes
      // AADB2C90091: ユーザーの取り消し。
      // AADB2C90273: 無効な応答が受信されました: '{0}'
      const errorMessage = (error as handleRedirectErrorType)?.errorMessage;
      if (errorMessage?.startsWith("AADB2C90091") || errorMessage?.startsWith("AADB2C90273")) {
        // キャンセル時はトップページに遷移
        handleRedirectError.value = error as handleRedirectErrorType;
        return null;
      }
      throw error;
    }
    initialRouteState.clearInitialHash();
    // console.log("clearInitialHash");

    // ログインが成功しており、かつ、activeAccount の設定がなければ1つ目をActiveにする
    const allAccounts = publicClientApplication.getAllAccounts();
    if (allAccounts.length > 0
      && !publicClientApplication.getActiveAccount()) {
      publicClientApplication.setActiveAccount(allAccounts[0]);
    }

    return publicClientApplication;
  };

  const getPublicClientApplication = async (): Promise<IPublicClientApplication | null> => {
    try {
      return await getPublicClientApplicationSub();
    }
    catch (e) {
      // console.error("MSAL初期化エラー", e);
      handleRedirectError.value = {
        errorMessage: JSON.stringify(e)
      };
      return null;
    }
  };

  const getActiveAccount = async (): Promise<AccountInfo | null> => {
    const pca = await getPublicClientApplication();
    return pca?.getActiveAccount() || null;
  };

  const loginRedirect = async () => {
    const pca = await getPublicClientApplication();
    try {
      await pca?.loginRedirect({
        scopes: MsalScopes,
      });
    }
    catch {
      // 処理が途中で失敗するようなケースでは キャッシュ削除を試みる
      await pca?.clearCache();
    }
  };

  const logoutRedirect = async () => {
    const pca = await getPublicClientApplication();
    const activeAccount = pca?.getActiveAccount();
    if (activeAccount) {
      try {
        await pca?.logoutRedirect({
          account: activeAccount
        });
      }
      catch {
      // 処理が途中で失敗するようなケースでは キャッシュ削除を試みる
        await pca?.clearCache();
      }
    }
  };

  return {
    getActiveAccount,
    loginRedirect,
    logoutRedirect,
    getHandleRedirectError: () => handleRedirectError.value,
  };
};
